import { Star, ExternalLink, ShieldCheck, KeyRound, MessageCircle, Home } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { track } from "@/lib/analytics";

const BADGES = [
  { icon: KeyRound, label: "Entire-villa privacy" },
  { icon: ShieldCheck, label: "Secure booking engine" },
  { icon: MessageCircle, label: "Direct WhatsApp support" },
  { icon: Home, label: "Book direct, no middlemen" },
];

export default function Reviews({ reviews, settings }) {
  const hasRating = reviews?.rating && reviews?.count;
  const hasItems = reviews?.items?.length > 0;
  return (
    <section id="reviews" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="reviews-section">
      <Reveal>
        <p className="eyebrow mb-4">Guest Words</p>
        <h2 className="max-w-3xl font-serif text-2xl leading-snug tracking-tight text-cream sm:text-3xl lg:text-4xl">
          Loved by those who <span className="italic text-copper-gradient">found us.</span>
        </h2>
      </Reveal>

      {hasRating && (
        <Reveal delay={0.1}>
          <div className="mt-10 flex items-center gap-4" data-testid="reviews-rating-summary">
            <span className="font-serif text-5xl text-copper-gradient">{reviews.rating}</span>
            <div>
              <div className="flex gap-1" aria-label={`${reviews.rating} out of 5 stars`}>
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-copper text-copper" />)}
              </div>
              <p className="mt-1 text-xs text-cream-muted">{reviews.count} verified guest reviews</p>
            </div>
          </div>
        </Reveal>
      )}

      {hasItems && (
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {reviews.items.map((r, i) => (
            <Reveal key={i} delay={0.08 * i}>
              <blockquote className="card-hover h-full rounded-2xl border border-white/10 bg-charcoal-800 p-6" data-testid={`review-card-${i}`}>
                <div className="flex gap-1">{[...Array(r.stars || 5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-copper text-copper" />)}</div>
                <p className="mt-4 text-sm font-light leading-relaxed text-cream-muted">"{r.text}"</p>
                <footer className="mt-4 text-xs font-mono uppercase tracking-[0.15em] text-copper-300">— {r.name}</footer>
              </blockquote>
            </Reveal>
          ))}
        </div>
      )}

      <Reveal delay={0.15}>
        <div className="mt-12 flex flex-wrap items-center gap-3">
          {BADGES.map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-2 rounded-full border border-copper/25 px-4 py-2 text-xs text-cream-muted">
              <Icon className="h-3.5 w-3.5 text-copper-400" /> {label}
            </span>
          ))}
        </div>
        {settings?.google_reviews_url && (
          <a href={settings.google_reviews_url} target="_blank" rel="noreferrer"
            onClick={() => track("reviews_link_click")}
            data-testid="read-all-reviews-link"
            className="mt-6 inline-flex items-center gap-2 text-sm text-copper-300 underline-offset-4 transition-colors hover:text-copper-400 hover:underline">
            {reviews?.note || "Read all reviews on Google"} <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </Reveal>
    </section>
  );
}
